import { Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import TextCell from "./TextCell";
import { makeStyles } from "@mui/styles";import { useState } from "react";
import { LivenessMode } from "../../../types/FaceVerification";
import { settingUseStyles } from "../Settings";
import { ISliderInterface } from "./SliderCell";
import ConfirmationDialog from "../../../components/Misc/Dialog/ConfirmationDialog";
import { FVAdvancedInitParams, FVInitParams } from "neurotec-megamatcherid-management-client";


const useStyles = makeStyles(() => ({
    title: {
        fontWeight: 500,
        fontSize: "1rem",
    },
    cell: {

    },
    cellBold: {
        fontWeight: 500
    },
    rowHead: {
        marginLeft: "1rem",
        backgroundColor: "#B6D3D3"
    },
    rowOdd: {
        backgroundColor: "#f4f8f5"
    },
    rowEven: {

    },
    button: {
        opacity: "100%",
        backgroundImage: "linear-gradient(90deg, #5da3a5, #518382)",
    },
    disabledButton: {
        opacity: "50%",
        backgroundImage: "linear-gradient(90deg, #5da3a5, #518382)",
    },
}))

interface IISOLivenessTable {
    options: FVInitParams
    setOption: (key: string, value: string | number | boolean, advanced?: boolean) => void,
    setLiveness: (liveness: LivenessMode) => void
    setQualityImageSize: (w: number, h: number) => void,
    active: boolean,
    findFVStringSetting: (name: string) => string,
    findDefaultFVSliderInterface: (name: string) => ISliderInterface | undefined,
    imageLivenessInputType: string[]
}

const ISOLivenessTable: React.FC<IISOLivenessTable> = (props) => {

    const classes = useStyles();
    const classesContainer = settingUseStyles()
    const [open, setOpen] = useState(false)
    
    const handleApply = () => {
        setOpen(true)
    }

    const findOption = (option: string): FVAdvancedInitParams | undefined => {
        return props.options.advancedParameters?.find((el) => el.key === option)
    }

    const handleSettings = () => {
        let imageSize = findOption("StrictQualityImageSize")
        if (imageSize) {
            imageSize.value = "921600"
        } else {
            imageSize = {key: "StrictQualityImageSize", value: "921600"}
            props.options.advancedParameters?.push(imageSize)
        }

        let threshold = findOption("LivenessThreshold")
        if (threshold) {
            threshold.value = "40"
        } else {
            threshold = {key: "LivenessThreshold", value: "40"}
            props.options.advancedParameters?.push(threshold)
        }

        let imageLivenessInputType = findOption("ImageLivenessInputType")
        if (imageLivenessInputType) {
            imageLivenessInputType.value = "1970183"
        } else {
            imageLivenessInputType = {key: "ImageLivenessInputType", value: "1970183"}
            props.options.advancedParameters?.push(imageLivenessInputType)
        }

        let imageLivenessOutputType = findOption("ImageLivenessOutputType")
        if (imageLivenessOutputType) {
            imageLivenessOutputType.value = "Min"
        } else {
            imageLivenessOutputType = {key: "ImageLivenessOutputType", value: "Min"}
            props.options.advancedParameters?.push(imageLivenessOutputType)
        }

        let passiveLivenessRequiredDetectionCount = findOption("PassiveLivenessRequiredDetectionCount")
        if (passiveLivenessRequiredDetectionCount) {
            passiveLivenessRequiredDetectionCount.value = "8"
        } else {
            passiveLivenessRequiredDetectionCount = {key: "PassiveLivenessRequiredDetectionCount", value: "8"}
            props.options.advancedParameters?.push(passiveLivenessRequiredDetectionCount)
        }

        props.setQualityImageSize(1280, 720)
        props.setLiveness(LivenessMode.PASSIVE)
    }

    const showCell = (text: string, bold?: boolean) => {
        return(
            <TableCell width="25%" align="left" className={bold? classes.cellBold : classes.cell}>
                {text}
            </TableCell>
        );
    }

    return(
        <>
            <TableContainer component={Paper} className={classesContainer.container}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TextCell head colspan={2} title="ISO 30103-7 Level 2 parameters" subtitle="By default, the MegaMatcher ID does not use the ISO 30107-3 parameter set."/>
                            <TableCell align="right">
                                <Button onClick={handleApply} className={props.active? classes.button : classes.disabledButton} color="primary" variant="contained" disabled={!props.active}>
                                    Apply
                                </Button>
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow className={classes.rowHead}>
                            {showCell("Parameter", true)}
                            {showCell("Value", true)}
                            {showCell("Default value", true)}
                        </TableRow>
                        <TableRow className={classes.rowOdd}>
                            {showCell("Image resolution", true)}
                            {showCell("1280x720")}
                            {showCell("1280x720")}
                        </TableRow>
                        <TableRow className={classes.rowEven}>
                            {showCell("Liveness mode", true)}
                            {showCell("Passive")}
                            {showCell("Passive")}
                        </TableRow>
                        <TableRow className={classes.rowOdd}>
                            {showCell("Liveness threshold", true)}
                            {showCell("40")}
                            {showCell(props.findDefaultFVSliderInterface("LivenessThreshold")?.value.toString() ?? "40")}
                        </TableRow>
                        <TableRow className={classes.rowEven}>
                            {showCell("Image liveness input type", true)}
                            {showCell("Type0, Type1, Type2, Type12, Type17, Type18, Type19, Type20")}
                            {showCell(props.imageLivenessInputType.join(", "))}
                        </TableRow>
                        <TableRow className={classes.rowOdd}>
                            {showCell("Image liveness output type", true)}
                            {showCell("Min")}
                            {showCell(props.findFVStringSetting("ImageLivenessOutputType"))}
                        </TableRow>
                        <TableRow className={classes.rowEven}>
                            {showCell("Passive liveness required detection count", true)}
                            {showCell("8")}
                            {showCell(props.findDefaultFVSliderInterface("PassiveLivenessRequiredDetectionCount")?.value.toString() ?? "8")}
                        </TableRow>
                        <TableRow>
                            <TableCell colSpan={3}>
                                <b>Note:</b> If PC uses external camera, the camera should be positioned is such way, that it is on top/bottom of the monitor and user could clearly se him/her self.<br/><br/>
                                Lower values are used that are still reliant for spoof prevention, yet make the capturing process easier for the user.<br/><br/>
                                PC values may also be used for MegaMatcherId Web service, yet it is strongly advised to stick to default configuration.
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>
            <ConfirmationDialog
                open={open}
                setOpen={setOpen}
                title={"Apply settings"}
                text={"Apply ISO/IEC 30107-3 Level 2 settings?"}
                cancelText={"Cancel"}
                confirmText={"Apply"}
                confirm={handleSettings}
                confirmColor={"primary"}
            />
        </>
    );
}

export default ISOLivenessTable